import React, { useRef, useState, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './PropertyImageUpload.scss';
import ZoomIcon from '../../BuildingModal/Comments/ZoomIcon';
import TrashIcon from '../../BuildingModal/Comments/TrashIcon';
import { getCookie } from '../../utils/utils';
import { UNSUPPORTED_IMAGE_ERROR } from '../../../hooks/useListingImageUpload';

export interface ImageData {
  id: string;
  url: string;
  thumbnailUrl: string;
  uploadedAt?: string;
  order?: number;
  isUploading?: boolean;
  isEnhancing?: boolean;
  uploadProgress?: number;
  error?: string;
  // AI Enhancement fields
  originalUrl?: string;
  enhancedUrl?: string;
  originalThumbnailUrl?: string;
  enhancedThumbnailUrl?: string;
  enhancementFailed?: boolean;
  isDisplayOptimized?: boolean;
}

interface EnhancementStatus {
  enabled: boolean;
  credits: number;
  price_per_package: number;
  credits_per_package: number;
}

interface PropertyImageUploadProps {
  images: ImageData[];
  setImages: React.Dispatch<React.SetStateAction<ImageData[]>>;
  maxImages?: number;
  enhancementStatus: EnhancementStatus | null;
  onEnhancementStatusUpdate?: (status: EnhancementStatus) => void;
  onOpenLightbox?: (index: number) => void;
  onBuyCredits?: () => void;
  isPurchasingCredits?: boolean;
  isLoggedIn: boolean;
  onUploadingChange?: (isUploading: boolean) => void;
  setLoginModalVisible?: (isVisible: boolean) => void;
  onImagesSelected?: () => void;
}

interface SortableImageProps {
  image: ImageData;
  index: number;
  onRemove: (index: number) => void;
  onOpenLightbox?: (index: number) => void;
  t: any;
  HEIC_PLACEHOLDER: string;
  isDeleteMode: boolean;
  isSelected: boolean;
  onToggleSelect: (index: number) => void;
}

/**
 * One sortable image with drag handle and order badge. A refused upload says why (unsupported format)
 * instead of a generic failure.
 */
const SortableImage: React.FC<SortableImageProps> = ({
  image,
  index,
  onRemove,
  onOpenLightbox,
  t,
  HEIC_PLACEHOLDER,
  isDeleteMode,
  isSelected,
  onToggleSelect,
}) => {
  const [hasBeenDragged, setHasBeenDragged] = React.useState(false);
  const [isHolding, setIsHolding] = React.useState(false);
  const holdTimerRef = React.useRef<NodeJS.Timeout>();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: image.id || `temp-${index}`,
    disabled: isDeleteMode // Disable dragging in delete mode
  });

  // Track if we're dragging to prevent click on drag end
  React.useEffect(() => {
    if (isDragging) {
      setHasBeenDragged(true);
      setIsHolding(false);
    } else if (hasBeenDragged) {
      // Reset after a short delay to prevent click after drag
      const timeout = setTimeout(() => setHasBeenDragged(false), 100);
      return () => clearTimeout(timeout);
    }
  }, [isDragging, hasBeenDragged]);

  // Add touch event handlers for visual feedback
  const handleTouchStart = React.useCallback(() => {
    if (!isDeleteMode) {
      setIsHolding(true);
      // Clear any existing timer
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
      }
      // Reset after the delay period if drag didn't start
      // Match the TouchSensor delay
      holdTimerRef.current = setTimeout(() => {
        setIsHolding(false);
      }, 350); // Slightly longer than TouchSensor delay to ensure visual feedback
    }
  }, [isDeleteMode]);

  const handleTouchEnd = React.useCallback(() => {
    setIsHolding(false);
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
    }
  }, []);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
      }
    };
  }, []);

  // Detect if we're on a touch device for UI adjustments
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`property-image-upload__preview ${image.isUploading ? 'is-uploading' : ''} ${image.error ? 'has-error' : ''} ${image.enhancedUrl ? 'is-enhanced' : ''} ${isDragging ? 'is-dragging' : ''} ${isSelected ? 'is-selected' : ''} ${isDeleteMode ? 'delete-mode' : ''} ${isHolding ? 'is-holding' : ''}`}
      onClick={() => {
        // Don't open lightbox if we just finished dragging
        if (hasBeenDragged) return;

        if (isDeleteMode) {
          onToggleSelect(index);
        } else if (!image.isUploading && !image.error && !isDragging && onOpenLightbox) {
          onOpenLightbox(index);
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onContextMenu={(e) => e.preventDefault()}
      // On touch devices, make the entire tile the drag handle
      {...(isTouchDevice && !isDeleteMode ? attributes : {})}
      {...(isTouchDevice && !isDeleteMode ? listeners : {})}
      aria-label={`${t('building.offMarket.modal.propertyImage')} ${index + 1}${index === 0 ? ` - ${t('building.offMarket.modal.coverPhoto')}` : ''}`}
      role="listitem"
    >
      {/* Selection Checkbox - visible in delete mode */}
      {isDeleteMode && (
        <div className="property-image-upload__select-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(index)}
            aria-label={`${t('building.offMarket.modal.selectImage')} ${index + 1}`}
            onClick={(e) => e.stopPropagation()}
          />
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" stroke="#667eea" strokeWidth="2" fill={isSelected ? '#667eea' : 'white'} />
            {isSelected && (
              <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            )}
          </svg>
        </div>
      )}

      {/* Drag Handle - desktop-only drag handle; on mobile it's just a visual affordance */}
      {!isDeleteMode && (
        <div
          className={`property-image-upload__drag-handle ${isTouchDevice ? 'property-image-upload__drag-handle--touch' : ''}`}
          {...(!isTouchDevice ? attributes : {})}
          {...(!isTouchDevice ? listeners : {})}
          aria-label={t('building.offMarket.modal.dragToReorder')}
          role="button"
          tabIndex={0}
          aria-roledescription="sortable">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="6" cy="4" r="1.5" fill="currentColor" />
            <circle cx="14" cy="4" r="1.5" fill="currentColor" />
            <circle cx="6" cy="10" r="1.5" fill="currentColor" />
            <circle cx="14" cy="10" r="1.5" fill="currentColor" />
            <circle cx="6" cy="16" r="1.5" fill="currentColor" />
            <circle cx="14" cy="16" r="1.5" fill="currentColor" />
          </svg>
        </div>
      )}

      {/* Order Badge */}
      <div className="property-image-upload__order-badge">
        {index + 1}
        {index === 0 && <span className="property-image-upload__cover-label">{t('building.offMarket.modal.coverPhoto')}</span>}
      </div>

      <img
        src={image.thumbnailUrl}
        alt={t('building.offMarket.modal.propertyImage')}
        className="property-image-upload__preview-img"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
        onError={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          if (img.src !== HEIC_PLACEHOLDER) {
            img.src = HEIC_PLACEHOLDER;
          }
        }}
      />

      {!image.isUploading && !image.error && (
        <div className="property-image-upload__ai-badge">
          <ZoomIcon color="#667eea" size={14} />
        </div>
      )}


      {image.isUploading && (
        <div className="property-image-upload__upload-progress">
          <div className="property-image-upload__progress-indicator"></div>
        </div>
      )}

      {image.error && (
        <div className="property-image-upload__error-overlay">
          <span className="property-image-upload__error-text">
            {t(
              image.error === UNSUPPORTED_IMAGE_ERROR
                ? 'building.offMarket.modal.unsupportedImage'
                : 'building.offMarket.modal.uploadFailed',
            )}
          </span>
        </div>
      )}

      {image.enhancementFailed && !image.error && (
        <div className="property-image-upload__enhancement-failed">
          {t('building.offMarket.modal.enhancementFailed')}
        </div>
      )}
    </div>
  );
};

const PropertyImageUpload: React.FC<PropertyImageUploadProps> = ({
  images = [],  // Add default value to ensure images is always an array
  setImages,
  maxImages = 30,
  enhancementStatus,
  onEnhancementStatusUpdate,
  onOpenLightbox,
  onBuyCredits,
  isPurchasingCredits = false,
  isLoggedIn,
  onUploadingChange,
  setLoginModalVisible,
  onImagesSelected,
}) => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputEnhanceRef = useRef<HTMLInputElement>(null);
  const fileInputEnhanceCameraRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<string>>(new Set());
  const [previewImage, setPreviewImage] = useState<{ url: string; title: string } | null>(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const photoOptionsRef = useRef<HTMLDivElement>(null);
  const languageIsEnglish = i18n.language.startsWith('en');

  // Custom PointerSensor that ONLY responds to mouse events, not touch
  class MouseOnlyPointerSensor extends PointerSensor {
    static activators = [
      {
        eventName: 'onPointerDown' as const,
        handler: ({ nativeEvent: event }: React.PointerEvent) => {
          // Only handle mouse events, ignore touch/pen
          if (event.pointerType === 'mouse') {
            return true;
          }
          return false;
        },
      },
    ];
  }

  // Configure sensors - MouseOnlyPointerSensor for mouse, TouchSensor for touch
  const sensors = useSensors(
    useSensor(MouseOnlyPointerSensor, {
      activationConstraint: {
        distance: 8, // Mouse needs 8px movement to start dragging
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300, // Touch needs 300ms hold to start dragging
        tolerance: 5, // Allow 5px movement during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Friendly SVG placeholder for formats the browser cannot preview (e.g., HEIC)
  const HEIC_PLACEHOLDER = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="320" height="240" viewBox="0 0 320 240">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#EAE7FF"/>
          <stop offset="100%" stop-color="#D9F0FF"/>
        </linearGradient>
      </defs>
      <rect rx="16" ry="16" width="100%" height="100%" fill="url(#g)"/>
      <g fill="none" stroke="#667eea" stroke-width="6">
        <circle cx="160" cy="104" r="26" stroke-opacity=".25"/>
        <path d="M160 78 a26 26 0 0 1 26 26" stroke-linecap="round"/>
      </g>
      <text x="160" y="164" text-anchor="middle" font-family="Inter,system-ui,Segoe UI,Roboto,Arial" font-size="16" fill="#4B5563">Loading photo…</text>
    </svg>
  `);

  // Ensure images is always an array
  const safeImages = Array.isArray(images) ? images : [];

  /**
   * Uploads the picked files through the enhance or plain route. A 400 becomes UNSUPPORTED_IMAGE_ERROR so the
   * tile can say the format was refused instead of a generic failure.
   */
  const handleImageUpload = useCallback(async (files: FileList, forceEnhance?: boolean) => {
    const fileArray = Array.from(files);

    if (safeImages.length + fileArray.length > maxImages) {
      setUploadError(t('building.offMarket.modal.imageLimitExceeded', { max: maxImages }));
      return;
    }

    // Accept normal image MIME types as well as HEIC/HEIF by extension
    const imageFiles = fileArray.filter(file => {
      const lowerName = (file.name || '').toLowerCase();
      const isImageMime = (file.type || '').startsWith('image/');
      const isHeifExt = lowerName.endsWith('.heic') || lowerName.endsWith('.heif');
      if (!isImageMime && isHeifExt) {
        console.log('Accepting HEIC/HEIF file by extension:', file.name, 'type:', file.type);
      }
      return isImageMime || isHeifExt;
    });

    const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
    const oversizedFiles = imageFiles.filter(f => f.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setUploadError(t('building.offMarket.modal.fileTooLarge', { max: '15MB' }));
      return;
    }

    // Only enhance when explicitly requested (via the Upload & Enhance button)
    const shouldEnhance = forceEnhance === true;

    const newImagesWithStatus = imageFiles.map(file => {
      const nameLower = (file.name || '').toLowerCase();
      const isHeic = nameLower.endsWith('.heic') || nameLower.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif';
      const objectUrl = isHeic ? HEIC_PLACEHOLDER : URL.createObjectURL(file);
      return {
        id: '',
        url: objectUrl,
        thumbnailUrl: objectUrl,
        isUploading: true,
        isEnhancing: shouldEnhance,
        uploadProgress: 0,
        error: undefined,
      } as ImageData;
    });

    // Capture start index to update specific items deterministically
    const startIndex = safeImages.length;
    setImages(prev => [...prev, ...newImagesWithStatus]);

    const endpoint = shouldEnhance ? '/ai/enhance-and-upload-image' : '/upload-property-image';

    // Inform parent that uploads are in progress
    onUploadingChange && onUploadingChange(true);

    // Helper: retry wrapper for transient gateway/server errors
    const fetchWithRetry = async (formData: FormData, attempts = 3): Promise<Response> => {
      let lastError: any = null;
      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          const resp = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'X-CSRF-TOKEN': getCookie('csrf_access_token'),
            },
            body: formData,
          });
          // Retry on 5xx and 502/504 gateway issues
          if ([500, 502, 503, 504].includes(resp.status)) {
            lastError = new Error(`Server error ${resp.status}`);
          } else {
            return resp;
          }
        } catch (err) {
          lastError = err;
        }
        // Exponential backoff
        await new Promise(r => setTimeout(r, 300 * Math.pow(2, attempt)));
      }
      throw lastError || new Error('Upload failed');
    };

    const maxConcurrent = 3; // throttle uploads in live envs to avoid gateway/worker OOM
    let pointer = 0;
    let active = 0;

    await new Promise<void>((resolve) => {
      const next = () => {
        // Completed all
        if (pointer >= imageFiles.length && active === 0) {
          onUploadingChange && onUploadingChange(false);
          return resolve();
        }
        while (active < maxConcurrent && pointer < imageFiles.length) {
          const localIndex = pointer++;
          const file = imageFiles[localIndex];
          active++;

          (async () => {
            const formData = new FormData();
            formData.append('image', file);
            if (shouldEnhance) {
              formData.append('enhance', 'true');
              formData.append('style', 'nordic');
            }

            try {
              const response = await fetchWithRetry(formData, 3);
              if (response.status === 400) {
                throw new Error(UNSUPPORTED_IMAGE_ERROR);
              }
              if (!response.ok) {
                throw new Error(`Upload failed with status ${response.status}`);
              }
              const data = await response.json();
              setImages(prev => {
                const newImages = [...prev];
                const targetIndex = startIndex + localIndex;
                if (newImages[targetIndex]) {
                  newImages[targetIndex] = {
                    id: data.id,
                    url: data.url || data.originalUrl,
                    thumbnailUrl: data.thumbnailUrl || data.originalThumbnailUrl,
                    isUploading: false,
                    uploadProgress: 100,
                    originalUrl: data.originalUrl,
                    enhancedUrl: data.enhancedUrl,
                    originalThumbnailUrl: data.originalThumbnailUrl,
                    enhancedThumbnailUrl: data.enhancedThumbnailUrl,
                    enhancementFailed: data.enhancementFailed,
                    isDisplayOptimized: data.isDisplayOptimized === true
                  };
                }
                return newImages;
              });

              if (data.enhancedUrl && enhancementStatus && onEnhancementStatusUpdate) {
                onEnhancementStatusUpdate({
                  ...enhancementStatus,
                  credits: Math.max(0, enhancementStatus.credits - 1)
                });
              }
            } catch (error) {
              setImages(prev => {
                const newImages = [...prev];
                const targetIndex = startIndex + localIndex;
                if (newImages[targetIndex]) {
                  newImages[targetIndex] = {
                    ...newImages[targetIndex],
                    isUploading: false,
                    isEnhancing: false,
                    error: error instanceof Error ? error.message : String(error)
                  };
                }
                return newImages;
              });
            } finally {
              // Revoke object URLs to avoid memory leaks
              URL.revokeObjectURL(newImagesWithStatus[localIndex].url);
              URL.revokeObjectURL(newImagesWithStatus[localIndex].thumbnailUrl);
              active--;
              next();
            }
          })();
        }
      };
      next();
    });

    setUploadError('');
  }, [safeImages, maxImages, enhancementStatus, onEnhancementStatusUpdate, setImages, t, onUploadingChange]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isLoggedIn) {
      // Prevent upload when not logged in; show login modal instead
      e.target.value = '';
      setLoginModalVisible?.(true);
      return;
    }
    if (e.target.files && e.target.files.length > 0) {
      handleImageUpload(e.target.files, false);
      e.target.value = '';
      // Notify parent that images were selected
      onImagesSelected?.();
    }
  }, [handleImageUpload, onImagesSelected, isLoggedIn, setLoginModalVisible]);

  const handleFileInputChangeWithEnhance = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleImageUpload(e.target.files, true);
      e.target.value = '';
      // Notify parent that images were selected
      onImagesSelected?.();
    }
  }, [handleImageUpload, onImagesSelected]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn) {
      // Block drop when not logged in
      setLoginModalVisible?.(true);
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(e.dataTransfer.files, false);
      // Notify parent that images were selected
      onImagesSelected?.();
    }
  }, [handleImageUpload, onImagesSelected, isLoggedIn, setLoginModalVisible]);

  const removeImage = useCallback((index: number) => {
    const imageToRemove = safeImages[index];

    if (imageToRemove.isUploading || imageToRemove.error) {
      URL.revokeObjectURL(imageToRemove.url);
      URL.revokeObjectURL(imageToRemove.thumbnailUrl);
    }

    setImages(prev => prev.filter((_, i) => i !== index));
  }, [safeImages, setImages]);

  const toggleImageSelection = useCallback((index: number) => {
    const imageId = safeImages[index]?.id || `temp-${index}`;
    setSelectedForDeletion(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  }, [safeImages]);

  const handleBatchDelete = useCallback(() => {
    setImages(prev => prev.filter((image, index) => {
      const imageId = image.id || `temp-${index}`;
      const shouldDelete = selectedForDeletion.has(imageId);

      // Clean up object URLs for deleted images
      if (shouldDelete && (image.isUploading || image.error)) {
        URL.revokeObjectURL(image.url);
        URL.revokeObjectURL(image.thumbnailUrl);
      }

      return !shouldDelete;
    }));

    // Reset selection state
    setSelectedForDeletion(new Set());
    setIsDeleteMode(false);
  }, [selectedForDeletion, setImages]);

  const cancelDeleteMode = useCallback(() => {
    setIsDeleteMode(false);
    setSelectedForDeletion(new Set());
  }, []);

  // Add keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete key to enter delete mode
      if (e.key === 'Delete' && !isDeleteMode && safeImages.length > 0) {
        e.preventDefault();
        setIsDeleteMode(true);
      }
      // Escape key to exit delete mode
      else if (e.key === 'Escape' && isDeleteMode) {
        e.preventDefault();
        cancelDeleteMode();
      }
      // Enter key to confirm deletion
      else if (e.key === 'Enter' && isDeleteMode && selectedForDeletion.size > 0) {
        e.preventDefault();
        handleBatchDelete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDeleteMode, safeImages.length, selectedForDeletion.size, cancelDeleteMode, handleBatchDelete]);

  // Handle click outside to close photo options
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (photoOptionsRef.current && !photoOptionsRef.current.contains(event.target as Node)) {
        setShowPhotoOptions(false);
      }
    };

    if (showPhotoOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPhotoOptions]);

  const triggerFileInput = () => {
    if (!isLoggedIn) {
      setLoginModalVisible?.(true);
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const triggerFileInputWithEnhance = () => {
    // On desktop, use the hidden input ref
    if (window.innerWidth >= 768 && fileInputEnhanceRef.current) {
      fileInputEnhanceRef.current.click();
    }
  };

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Handle drag end for reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setImages((items) => {
        const oldIndex = items.findIndex(
          (item) => (item.id || `temp-${items.indexOf(item)}`) === active.id
        );
        const newIndex = items.findIndex(
          (item) => (item.id || `temp-${items.indexOf(item)}`) === over?.id
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          return arrayMove(items, oldIndex, newIndex);
        }
        return items;
      });
    }

    setActiveId(null);
  };

  // Get sortable items IDs
  const sortableItems = safeImages.map((image, index) => image.id || `temp-${index}`);

  return (
    <div className="property-image-upload">
      <h4 className="property-image-upload__title">
        <Trans i18nKey="building.offMarket.modal.addPropertyImages" />
        <span className="property-image-upload__count">
          {safeImages.length}/{maxImages}
        </span>
      </h4>

      {/* AI Enhancement Promotion Card */}
      <div className="property-image-upload__ai-enhancement-card" onClick={(e) => e.stopPropagation()}>
        <div className="property-image-upload__ai-enhancement-gradient">
          <div className="property-image-upload__ai-enhancement-content">
            <div className="property-image-upload__ai-enhancement-icon">
              <div
                className="property-image-upload__ai-enhancement-icon__before"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewImage({
                    url: 'https://jaakkocom.blob.core.windows.net/jaakkocom-assets/before.jpg',
                    title: t('building.offMarket.modal.before')
                  });
                }}
                style={{ cursor: 'pointer' }}
              >
                <p className="property-image-upload__ai-enhancement-icon__before-text"><strong>
                  {t('building.offMarket.modal.before')}</strong></p>
                <img src="https://jaakkocom.blob.core.windows.net/jaakkocom-assets/before.jpg" height="112px" alt="Before" />
              </div>
              <div
                className="property-image-upload__ai-enhancement-icon__after"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewImage({
                    url: 'https://jaakkocom.blob.core.windows.net/jaakkocom-assets/after.jpg',
                    title: t('building.offMarket.modal.after')
                  });
                }}
                style={{ cursor: 'pointer' }}
              >
                <p className="property-image-upload__ai-enhancement-icon__after-text"><strong>
                  {t('building.offMarket.modal.after')}</strong></p>
                <img src="https://jaakkocom.blob.core.windows.net/jaakkocom-assets/after.jpg" height="112px" alt="After" />
              </div>
            </div>
            <h5 className="property-image-upload__ai-enhancement-title">
              {t('building.offMarket.modal.aiEnhancementTitle')}
            </h5>
            <p className="property-image-upload__ai-enhancement-text">
              <Trans
                i18nKey="building.offMarket.modal.aiEnhancementDescription"
                components={{
                  1: <strong />
                }}
              />{' '}
              {/*               {(!enhancementStatus || enhancementStatus.credits === 0) && <Trans
                i18nKey="building.offMarket.modal.aiEnhancementNormalPrice"
              />} */}
            </p>
            <p className="property-image-upload__ai-enhancement-text">
              <a
                href={languageIsEnglish ?
                  'https://info.jaakko.com/en-gb/styling-sales-photos-with-jaakko'
                  : 'https://info.jaakko.com/myyntikuvien-stailaus-jaakon-avulla'}
                target="_blank" rel="noopener noreferrer"
              >
                {t('building.offMarket.modal.aiEnhancementDescriptionReadMoreLink')}
              </a>
            </p>
            {enhancementStatus && enhancementStatus.credits > 0 ? (
              <>
                <div className="property-image-upload__ai-credits-info">
                  <span className="property-image-upload__ai-credits-badge">
                    {t('building.offMarket.modal.creditsAvailable', { count: enhancementStatus.credits })}
                  </span>
                </div>
                <div style={{ position: 'relative' }}>
                  <button
                    className="property-image-upload__ai-enhancement-button property-image-upload__ai-enhancement-button--enhancement-enabled"
                    onClick={(e) => {
                      e.stopPropagation();
                      // On mobile, show photo options; on desktop, directly open file picker
                      if (window.innerWidth < 768) {
                        setShowPhotoOptions(!showPhotoOptions);
                      } else {
                        triggerFileInputWithEnhance();
                      }
                    }}
                  >
                    <span className="property-image-upload__ai-enhancement-button-text">
                      {t('building.offMarket.modal.uploadAndEnhance')}
                    </span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {/* Photo options dropdown for mobile */}
                  {showPhotoOptions && window.innerWidth < 768 && (
                    <div
                      ref={photoOptionsRef}
                      style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 12px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '180px',
                        backgroundColor: '#018860',
                        padding: '12px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 10,
                        animation: 'fadeInUp 0.2s ease-out'
                      }}
                    >
                      <style>{`
                        @keyframes fadeInUp {
                          from {
                            opacity: 0;
                            transform: translateX(-50%) translateY(10px);
                          }
                          to {
                            opacity: 1;
                            transform: translateX(-50%) translateY(0);
                          }
                        }
                      `}</style>

                      <label
                        htmlFor="upload-photo-enhance"
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: '#f9f9f9',
                          borderRadius: '6px',
                          border: '1px solid #f9f9f9',
                          padding: '12px',
                          marginBottom: '12px',
                          color: '#202125',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="file"
                          id="upload-photo-enhance"
                          accept="image/*,.heic,.heif"
                          multiple
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            handleFileInputChangeWithEnhance(e);
                            setShowPhotoOptions(false);
                          }}
                        />
                        {t('building.offMarket.modal.pickPhotos')}
                      </label>

                      <label
                        htmlFor="take-photo-enhance"
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          border: '1px solid #f9f9f9',
                          padding: '12px',
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="file"
                          id="take-photo-enhance"
                          accept="image/*"
                          capture="environment"
                          multiple
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            handleFileInputChangeWithEnhance(e);
                            setShowPhotoOptions(false);
                          }}
                        />
                        {t('building.comments.takePhoto')}
                      </label>

                      {/* Arrow pointing down */}
                      <div style={{
                        position: 'absolute',
                        bottom: '-6px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '0',
                        height: '0',
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderTop: '8px solid #018860'
                      }} />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="property-image-upload__ai-credits-info">
                  <span className="property-image-upload__ai-credits-badge">
                    {!isLoggedIn ? t('building.offMarket.modal.firstPhotosFreeOfCharge') :
                      t('building.offMarket.modal.creditsPricing', {
                        price: enhancementStatus?.price_per_package?.toFixed(2) || '9.90',
                        count: enhancementStatus?.credits_per_package || 60
                      })}
                  </span>
                </div>
                <button
                  className="property-image-upload__ai-enhancement-button property-image-upload__ai-enhancement-button--purchase"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('PropertyImageUpload Buy Credits clicked, isLoggedIn:', isLoggedIn);
                    // Always call onBuyCredits - the parent component will handle login checks and form saving
                    if (onBuyCredits) {
                      onBuyCredits();
                    } else {
                      console.error('onBuyCredits is not defined');
                    }
                  }}
                  disabled={isPurchasingCredits}
                >
                  <span className="property-image-upload__ai-enhancement-button-text">
                    {(isPurchasingCredits ? t('building.offMarket.modal.processing') : t('building.offMarket.modal.uploadAndEnhance'))}
                  </span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

       {/* Hidden file input for AI enhancement (desktop only) */}
       <input
        ref={fileInputEnhanceRef}
        type="file"
        multiple
        accept="image/*,.heic,.heif"
        style={{ display: 'none' }}
        onChange={handleFileInputChangeWithEnhance}
      />

      <div className="property-image-upload__separator-wrapper">
        <div className="property-image-upload__separator-line" />
        <div
          className="property-image-upload__separator-text"
        >{t('building.offMarket.modal.or')}</div>
        <div className="property-image-upload__separator-line" />
      </div>

      {/* Regular upload dropzone */}
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={triggerFileInput}
        className="property-image-upload__dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.heic,.heif"
          className="property-image-upload__file-input"
          onChange={handleFileInputChange}
        />
        <div className="property-image-upload__dropzone-content">
          <div className="property-image-upload__upload-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M12 5L7 10M12 5L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="property-image-upload__dropzone-text">
            <Trans i18nKey="building.offMarket.modal.dragImagesHere" />
          </p>
        </div>
      </div>

      {uploadError && (
        <div className="property-image-upload__error">
          {uploadError}
        </div>
      )}

      {safeImages.length > 1 && !isDeleteMode && (
        <div className="property-image-upload__reorder-hint">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="6" cy="4" r="1" fill="currentColor" />
            <circle cx="14" cy="4" r="1" fill="currentColor" />
            <circle cx="6" cy="10" r="1" fill="currentColor" />
            <circle cx="14" cy="10" r="1" fill="currentColor" />
            <circle cx="6" cy="16" r="1" fill="currentColor" />
            <circle cx="14" cy="16" r="1" fill="currentColor" />
          </svg>
          <span className="property-image-upload__reorder-hint-text">
            <span className="property-image-upload__reorder-hint-desktop">
              {t('building.offMarket.modal.reorderHint')}
            </span>
            <span className="property-image-upload__reorder-hint-mobile">
              {t('building.offMarket.modal.reorderHintMobile')}
            </span>
          </span>
        </div>
      )}

      {safeImages.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          accessibility={{
            announcements: {
              onDragStart: ({ active }) => {
                const index = safeImages.findIndex((img, idx) => (img.id || `temp-${idx}`) === active.id);
                return `Picked up image ${index + 1}. Use arrow keys to reorder.`;
              },
              onDragOver: ({ active, over }) => {
                if (over) {
                  const activeIndex = safeImages.findIndex((img, idx) => (img.id || `temp-${idx}`) === active.id);
                  const overIndex = safeImages.findIndex((img, idx) => (img.id || `temp-${idx}`) === over.id);
                  return `Image ${activeIndex + 1} is now in position ${overIndex + 1}`;
                }
                return '';
              },
              onDragEnd: ({ active, over }) => {
                if (over && active.id !== over.id) {
                  const activeIndex = safeImages.findIndex((img, idx) => (img.id || `temp-${idx}`) === active.id);
                  const overIndex = safeImages.findIndex((img, idx) => (img.id || `temp-${idx}`) === over.id);
                  return `Image moved from position ${activeIndex + 1} to position ${overIndex + 1}`;
                }
                return 'Image dropped';
              },
              onDragCancel: ({ active }) => {
                const index = safeImages.findIndex((img, idx) => (img.id || `temp-${idx}`) === active.id);
                return `Reordering cancelled. Image ${index + 1} returned to its position.`;
              },
            },
          }}
        >
          <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
            <div className="property-image-upload__preview-grid" role="list">
              {safeImages.map((image, index) => {
                const imageId = image.id || `temp-${index}`;
                return (
                  <SortableImage
                    key={imageId}
                    image={image}
                    index={index}
                    onRemove={removeImage}
                    onOpenLightbox={onOpenLightbox}
                    t={t}
                    HEIC_PLACEHOLDER={HEIC_PLACEHOLDER}
                    isDeleteMode={isDeleteMode}
                    isSelected={selectedForDeletion.has(imageId)}
                    onToggleSelect={toggleImageSelection}
                  />
                );
              })}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <div className="property-image-upload__drag-overlay">
                <img
                  src={safeImages.find((img, idx) => (img.id || `temp-${idx}`) === activeId)?.thumbnailUrl}
                  alt="Dragging image"
                  className="property-image-upload__preview-img"
                />
                <div className="property-image-upload__drag-overlay-badge">
                  {safeImages.findIndex((img, idx) => (img.id || `temp-${idx}`) === activeId) + 1}
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Delete Mode Toggle Button */}
      {safeImages.length > 0 && (
        <div className="property-image-upload__delete-controls">
          {!isDeleteMode ? (
            <button
              className="property-image-upload__delete-toggle"
              onClick={() => setIsDeleteMode(true)}
              aria-label={t('building.offMarket.modal.selectImagesToRemove')}
            >
              <TrashIcon size="16px" color="#667eea" />
              <span>{t('building.offMarket.modal.selectImagesToRemove')}</span>
            </button>
          ) : (
            <div className="property-image-upload__delete-actions">
              <button
                className="property-image-upload__cancel-delete"
                onClick={cancelDeleteMode}
              >
                {t('common.cancel')}
              </button>
              <button
                className="property-image-upload__confirm-delete"
                onClick={handleBatchDelete}
                disabled={selectedForDeletion.size === 0}
              >
                {selectedForDeletion.size > 0
                  ? t('building.offMarket.modal.removeSelected', { count: selectedForDeletion.size })
                  : t('building.offMarket.modal.selectImages')
                }
              </button>
            </div>
          )}
        </div>
      )}

      {/* Simple Image Preview Modal */}
      {previewImage && (
        <div
          className="property-image-upload__preview-modal"
          onClick={() => setPreviewImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            cursor: 'pointer',
          }}
        >
          <div
            className="property-image-upload__preview-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: window.innerWidth >= 768 ? 'auto' : '90%',
              maxWidth: window.innerWidth >= 768 ? '800px' : '90%',
              maxHeight: '90vh',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              cursor: 'default',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e5e5',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#1a1a1a'
              }}>
                {previewImage.title}
              </h3>
              <button
                onClick={() => setPreviewImage(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s',
                  color: '#666'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{
              padding: '20px',
              overflow: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1
            }}>
              <img
                src={previewImage.url}
                alt={previewImage.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: window.innerWidth >= 768 ? 'calc(90vh - 120px)' : 'auto',
                  height: 'auto',
                  width: 'auto',
                  display: 'block',
                  borderRadius: '8px',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyImageUpload;
